import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Modal,
  Stack,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { GridColDef } from "@mui/x-data-grid";
import { ReactComponent as ModifyIcon } from "../../../../../../assets/icons/MedintModifyIcon.svg";
import { ReactComponent as DeleteIcon } from "../../../../../../assets/icons/MedintDeleteIcon.svg";
import { ReactComponent as AddIcon } from "../../../../../../assets/icons/MedintAddIcon.svg";
import { MedintDataContext } from "../../../../Context/MedintContextWrapper";
import { medpgmpc, ricMedpgmpc } from "../../../../../../networking";
import { AllertMessage } from "./MedintAgendaForm/MedintAgendaFormDetail";
import GestioneCombinateModalDetails from "./GestioneCombinateModalDetails";
import { RHFAutocomplete } from "../../../../../RHFComponents/RHFComponents";
import { StyledDataGrid } from "../../../../../AppointmentManagement/AppointmentManagementDetail/AppointmentManagementDetail";


type Props = {
  open: boolean;
  onClose: () => void;
  selectedRow?: any;
};

type GestioneCombinateForm = {
  codiceMedico: string | null;
  specialita: string;
  prestazione: string;
  tipologia: string;
};

type MEDPGMPCDATA = {
  MEDPGMPC_PRGINT: string;
  MEDPGMP_PRGINT: string;
  ISTCOM_CODICE: string;
  ANAPRES_SISS_COD: string;
  MEDPGMPC_PID_ACU: string;
  MEDPGMPC_TIMESTAMP: string;
  MEDPGMPC_DURATA: string;
  DECODE_MEDPGMP_ANAPRE_COD: string;
  DECODE_MEDPGMP_SPECIA_CODICE: string;
  DECODE_ANAPRES_ANAPRES_SISS_DESCR: string;
  DECODE_ANAPRES_ANAPRES_FLAG_DISATTIVATO: string;
  DECODE_ISTCOM_ISTCOM_DESCRIZIONE: string;
};

const GestioneCombinateModal = ({ open, onClose, selectedRow }: Props) => {
  const context = useContext(MedintDataContext);

  // Dati restituiti dalla API ricMedpgmpc
  const [rowsData, setRowsData] = useState<MEDPGMPCDATA[]>();

  const [loading, setLoading] = useState<boolean>(false);

  const [showAllert, setShowAllert] = useState(false);

  const [severity, setSeverity] = useState<"warning" | "success" | "error">(
    "warning",
  );
  const [message, setMessage] = useState("");

  // Stato per aprire il modale di dettaglio per la gestione delle combinate
  const [openModal, setOpenModal] = useState<boolean>(false);

  // Row selezionata da modificare nel modale dettaglio
  const [selectedCombinataRow, setSelectedCombinataRow] = useState<any | null>(
    null,
  );
  //Stato per passare l'operazione da eseguire nel modale di dettaglio
  const [crudOperation, setCrudOperation] = useState<"insert" | "edit">(
    "insert",
  );

  // Row in attesa di conferma eliminazione (per l'alert)
  const [pendingDeleteRow, setPendingDeleteRow] = useState<any | null>(null);

  /**
   * Form locale utilizzato solo per visualizzare le informazioni della prestazione selezionata
   * I campi sono tutti disabilitati perché non devono essere modificati qui, fanno solo da reminder all'utente.
   */
  const methods = useForm<GestioneCombinateForm>({
    defaultValues: {
      codiceMedico: null,
      specialita: "",
      prestazione: "",
      tipologia: "",
    },
  });

  const { control, reset } = methods;

  /**
   * Costruisco le option degli autocomplete
   * partendo dai dati della row selezionata,
   * Uso useMemo per evitare di dover ricreare le options ad ogni render.
   */
  const specialitaOptions = useMemo(
    () =>
      selectedRow?.specialita
        ? [
            {
              value: selectedRow.specialita,
              label: selectedRow.specialita,
            },
          ]
        : [],
    [selectedRow],
  );

  const prestazioneOptions = useMemo(
    () =>
      selectedRow
        ? [
            {
              value: selectedRow.prestazione ?? "",
              label: `${selectedRow.descrizione ?? ""}`.trim(),
            },
          ]
        : [],
    [selectedRow],
  );

  const tipologiaOptions = useMemo(
    () =>
      selectedRow?.codiceAgenda || selectedRow?.tipologia
        ? [
            {
              value: selectedRow.codiceAgenda ?? selectedRow.tipologia,
              label: selectedRow.tipologia ?? "",
            },
          ]
        : [],
    [selectedRow],
  );

  /*
   * Funzione che recupera le combinazioni associate alla prestazione selezionata
   * salvo tutto in rowsdata
   * e successivamente mappo i dati per il DataGrid.
   */
  const getMedpgmpcData = async () => {
    setLoading(true);
    try {
      const response = await ricMedpgmpc({
        medpgmp_prgint: selectedRow?.idMedpgmp ?? "",
      });
      const raw = response?.data?.dati?.medpgmpc;

      const data: MEDPGMPCDATA[] = Array.isArray(raw) ? raw : [];

      setRowsData(data);

      return data;
    } catch (error) {
      console.error(error);
      setRowsData([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  /*
   * Mapping dei dati restituiti dall'API nella struttura richiesta dalla DataGrid
   * Aggiungo anche il campo _raw così mi rimane anche l'intero oggetto originale,
   * che è utile per le operazioni di inserimento e/o modifica
   */
  const combinateRows = useMemo(() => {
    return (rowsData ?? []).map((x) => ({
      id: x.MEDPGMPC_PRGINT,
      prgint: x.MEDPGMPC_PRGINT,
      medpgmpPrgint: x.MEDPGMP_PRGINT,
      codIstanzaCombinata: x.ISTCOM_CODICE,
      descrIstanzaCombinata: x.DECODE_ISTCOM_ISTCOM_DESCRIZIONE,
      codiceSISS: x.ANAPRES_SISS_COD,
      durata: x.MEDPGMPC_DURATA,
      _raw: x,
    }));
  }, [rowsData]);

  useEffect(() => {
    if (!open || !selectedRow) return;

    getMedpgmpcData();

    reset({
      codiceMedico:
        context?.selectedDoctor?.IDMEDICO ??
        context?.selectedDoctor?.pkey ??
        null,
      specialita: selectedRow.specialita ?? "",
      prestazione: selectedRow.prestazione ?? "",
      tipologia: selectedRow.codiceAgenda ?? selectedRow.tipologia ?? "",
    });
  }, [open, selectedRow, reset, context?.selectedDoctor]);

  //Funzione per aprire il modale di dettaglio con l'azione specifica da eseguire
  const handleOpenDetails = (
    operation: "insert" | "edit",
    row?: any | null,
  ) => {
    setCrudOperation(operation);
    //Se ho row la passo, altrimenti setto null
    setSelectedCombinataRow(row ?? null);
    setOpenModal(true);
  };

  //Funzione di chiusura del dettaglio.
  //Resetto i valori in modo che non vengano mantenuti nel form
  const handleCloseDetails = () => {
    setOpenModal(false);
    setSelectedCombinataRow(null);
    setCrudOperation("insert");
  };

  //Funzione di chiusura del modale e che svuota il form
  const handleClose = () => {
    reset({
      codiceMedico: null,
      specialita: "",
      prestazione: "",
      tipologia: "",
    });
    onClose();
  };

  const columns: GridColDef[] = [
    {
      field: "codIstanzaCombinata",
      headerName: "Cod. Istanza Combinata",
      flex: 1.2,
    },
    {
      field: "descrIstanzaCombinata",
      headerName: "Descr. Istanza Combinata",
      flex: 1.4,
    },
    {
      field: "codiceSISS",
      headerName: "Codice SISS",
      flex: 1,
    },
    {
      field: "durata",
      headerName: "Durata",
      flex: 0.6,
    },
    {
      field: "azioni",
      headerName: "Azioni",
      flex: 0.8,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <IconButton
            size="small"
            aria-label="Modifica"
            sx={{ color: "#024E88" }}
            onClick={() => handleOpenDetails("edit", params.row)}
          >
            <ModifyIcon />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Elimina"
            onClick={() => handleAskDeleteRow(params.row)}
            sx={{ color: "#d32f2f" }}
          >
            <DeleteIcon />
          </IconButton>
        </Stack>
      ),
    },
  ];

  const handleAskDeleteRow = (row: any) => {
    setPendingDeleteRow(row);
    setSeverity("warning");
    setMessage(`Confermi la cancellazione dell'istanza combinata?`);
    setShowAllert(true);
  };

  //Funzione di cancellazione di una row nella tabella
  const handleDeleteRow = async (row: any) => {
    setLoading(true);

    console.log("row da eliminare:", row);
    try {
      const response = await medpgmpc({
        crud: "d",
        prgint: row?.prgint ?? "",
      });

      if (response?.data?.esito === "OK") {
        await getMedpgmpcData();

        setSeverity("success");
        setMessage(
          response?.data?.messaggio ??
            "Istanza combinata eliminata correttamente",
        );
        setShowAllert(true);
        setPendingDeleteRow(null);
      } else {
        setSeverity("error");
        setMessage(
          response?.data?.messaggio ||
            "Errore durante l'eliminazione dell'istanza combinata",
        );
        setShowAllert(true);
      }
    } catch (error) {
      console.error(error);
      setSeverity("error");
      setMessage("Errore durante l'eliminazione dell'istanza combinata");
      setShowAllert(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!showAllert) return;
    if (severity === "warning") return;

    const t1 = setTimeout(() => setShowAllert(false), 4000);
    const t2 = setTimeout(() => {
      setSeverity("warning");
      setMessage("");
      setPendingDeleteRow(null);
    }, 6000);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [showAllert, severity]);

  return (
    <>
      <AllertMessage
        dataToSend={pendingDeleteRow}
        showAllert={showAllert}
        setShowAllert={setShowAllert}
        severity={severity}
        message={message}
        submitFunction={async (data: any) => {
          if (loading) return;

          if (severity === "warning") {
            await handleDeleteRow(data);
            return;
          }

          setShowAllert(false);
        }}
      />
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 9999,
          }}
        >
          <CircularProgress size="6rem" />
        </Box>
      )}

      {openModal && (
        <GestioneCombinateModalDetails
          open={openModal}
          onClose={handleCloseDetails}
          selectedRow={selectedCombinataRow}
          action={crudOperation}
          specialita={selectedRow.specialita}
          prestazione={selectedRow.prestazione}
          idMedpgmp={selectedRow.idMedpgmp}
          onSaved={getMedpgmpcData}
        />
      )}

      <Modal
        open={open}
        onClose={(_, reason) => {
          if (reason === "backdropClick") return;
          if (reason === "escapeKeyDown") return;
          handleClose();
        }}
      >
        <Box
          sx={{
            width: "92vw",
            maxWidth: 980,
            maxHeight: "90vh",
            bgcolor: "white",
            borderRadius: 2,
            boxShadow: 24,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            p: 2,
          }}
        >
          <Typography
            sx={{
              fontWeight: 500,
              mb: 2,
              color: "var(--text-primary, rgba(0, 0, 0, 0.87))",
              fontFamily: "Rubik",
              fontSize: "16px",
              fontStyle: "normal",
              lineHeight: "24px",
              letterSpacing: "-0.16px",
            }}
          >
            Gestione Combinate
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <RHFAutocomplete
                control={control}
                name="codiceMedico"
                label="Medico"
                options={context?.doctorsData ?? []}
                keyField="IDMEDICO"
                getOptionLabel={(d: any) =>
                  `${d.CODICEMEDICO ?? d["codice medico"] ?? ""} - ${
                    d.COGNOMEMEDICO ?? d.cognome ?? ""
                  } ${d.NOMEMEDICO ?? d.nome ?? ""}`.trim()
                }
                mapIn={(id, opts) =>
                  opts.find(
                    (o: any) => String(o.IDMEDICO ?? o.pkey) === String(id),
                  ) ??
                  context?.doctorsData?.find(
                    (e: any) =>
                      e["codice medico"] ===
                      context?.selectedDoctor?.["codice medico"],
                  ) ??
                  null
                }
                mapOut={(opt: any) =>
                  String(opt?.IDMEDICO ?? opt?.pkey ?? "") || null
                }
                disabled
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <RHFAutocomplete
                name="specialita"
                label="Specialità"
                control={control}
                options={specialitaOptions}
                getOptionLabel={(o: any) => o?.label ?? ""}
                mapIn={(value, opts) =>
                  opts.find((o: any) => String(o.value) === String(value)) ??
                  null
                }
                mapOut={(opt: any) => opt?.value ?? ""}
                disabled
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <RHFAutocomplete
                name="prestazione"
                label="Prestazione"
                control={control}
                options={prestazioneOptions}
                getOptionLabel={(o: any) => o?.label ?? ""}
                mapIn={(value, opts) =>
                  opts.find((o: any) => String(o.value) === String(value)) ??
                  null
                }
                mapOut={(opt: any) => opt?.value ?? ""}
                disabled
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <RHFAutocomplete
                name="tipologia"
                label="Tipologia"
                control={control}
                options={tipologiaOptions}
                getOptionLabel={(o: any) => o?.label ?? ""}
                mapIn={(value, opts) =>
                  opts.find((o: any) => String(o.value) === String(value)) ??
                  null
                }
                mapOut={(opt: any) => opt?.value ?? ""}
                disabled
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  mt: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  overflow: "hidden",
                  p: 2,
                }}
              >
                <Typography
                  sx={{
                    mb: 2,
                    fontWeight: 600,
                    color: "var(--text-secondary, rgba(0, 0, 0, 0.60))",
                  }}
                >
                  Istanza combinata
                </Typography>

                <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
                  <Button
                    variant="outlinedCustom"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDetails("insert")}
                  >
                    Inserisci
                  </Button>
                </Stack>

                <StyledDataGrid
                  rows={combinateRows}
                  columns={columns}
                  hideFooter
                  disableSelectionOnClick
                  getRowId={(row) => row.id}
                  sx={{
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: "#003B7A",
                      color: "#fff",
                    },
                    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                      {
                        outline: "none",
                      },
                    minHeight: 250,
                    overflow: "auto",
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Stack direction="row" justifyContent="space-between" mt={1}>
                <Button variant="outlinedCustom" onClick={handleClose}>
                  Indietro
                </Button>

                <Button
                  variant="containedCustom"
                  onClick={onClose}
                >
                  Conferma
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </>
  );
};

export default GestioneCombinateModal;
